import { AUTH_STORAGE_KEYS } from "@/constants/authStorageKeys";
import { E2E_DISABLE_CURSOR_AUTO_LOGIN_KEY } from "@/constants/e2eDev";
import { applyDeploymentRoutingTierFromUserProfile } from "@/utils/deploymentRouting";

type DevAutoLoginResponse = {
  success?: boolean;
  mfa_required?: boolean;
  auth_token?: string;
  user?: Record<string, unknown>;
};

const DEV_AUTO_LOGIN_PATH = "/__cursor/dev-auto-login";

export function isCursorDevAutoLoginClientEnabled(): boolean {
  return (
    import.meta.env.DEV &&
    import.meta.env.VITE_CURSOR_AUTO_LOGIN === "true"
  );
}

/**
 * When CURSOR_AUTO_LOGIN is enabled on the Vite dev server, establish a session
 * without visiting /login. Credentials never enter the client bundle.
 */
export async function tryCursorDevAutoLogin(): Promise<boolean> {
  if (!isCursorDevAutoLoginClientEnabled()) {
    return false;
  }

  if (sessionStorage.getItem(E2E_DISABLE_CURSOR_AUTO_LOGIN_KEY) === "1") {
    return false;
  }

  try {
    const response = await fetch(DEV_AUTO_LOGIN_PATH, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      console.warn(
        "[cursorDevAutoLogin] bootstrap failed:",
        response.status,
        await response.text().catch(() => ""),
      );
      return false;
    }

    const data = (await response.json()) as DevAutoLoginResponse;

    if (data.mfa_required) {
      console.warn(
        "[cursorDevAutoLogin] MFA required — auto-login skipped; use /login",
      );
      return false;
    }

    if (!data.success || !data.user) {
      console.warn("[cursorDevAutoLogin] login response missing user");
      return false;
    }

    if (data.auth_token) {
      localStorage.setItem(
        AUTH_STORAGE_KEYS.AUTH_DATA,
        JSON.stringify({
          auth_token: data.auth_token,
          user: data.user,
          last_updated: Date.now(),
        }),
      );
    }

    applyDeploymentRoutingTierFromUserProfile(data.user);
    return true;
  } catch (error) {
    console.warn("[cursorDevAutoLogin] bootstrap error:", error);
    return false;
  }
}
